import * as React from 'react';

import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { UseFormReturn } from 'react-hook-form';

import { DataTable } from '@/components/data-table/data-table';
import {
  ConflictFieldConfig,
  ConflictFieldRow,
  ConflictSnapshot,
} from '@/modules/projects/types/project-conflicts';

import { createConflictColumns } from '../../../../constants/conflicts-table-columns';

export interface ConflictsTableProps<TFieldKey extends string = string> {
  /** Table data rows */
  tableData: ConflictFieldRow[];

  /** Grouped data for sub-headers (optional) */
  groupedData?: Array<{ category: string; items: ConflictFieldRow[] }>;

  /** Snapshot data for column headers */
  snapshots: ConflictSnapshot[];

  /** Selected date range for the new change */
  selectedDateRange: { from: Date; to?: Date | null };

  /** Field configurations */
  fields: ConflictFieldConfig<TFieldKey>[];

  /** React Hook Form instance for value selection */
  form: UseFormReturn<any>;

  /** Translation function */
  t: TFunction;

  /** Translation namespace for field labels (used for grouped data) */
  translationNamespace: string;

  /** Optional context data to pass to field formatters (e.g., currency) */
  context?: any;

  /** Current form values from the parent form — used as fallback for isDisabled checks on fields not in the conflict form */
  currentFormValues?: Record<string, unknown>;
}

/**
 * Generic conflicts table component
 * Displays conflict resolution data with sticky header and sub-header support
 */
export const ConflictsTable = <TFieldKey extends string = string>({
  tableData,
  groupedData,
  snapshots,
  selectedDateRange,
  fields,
  form,
  t,
  context,
  currentFormValues,
}: ConflictsTableProps<TFieldKey>) => {
  // Create column definitions
  const columns = React.useMemo(
    () =>
      createConflictColumns({
        snapshots,
        selectedDateRange,
        fields,
        form,
        context,
        currentFormValues,
      }),
    [snapshots, selectedDateRange, fields, form, context, currentFormValues]
  );

  // Initialize table
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.fieldKey,
  });

  // Render group header for sub-headers
  const renderGroupHeader = React.useCallback(
    (category: string) => (
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t(`${category}`)}
      </div>
    ),
    [t]
  );

  return (
    <DataTable
      table={table}
      stickyHeader={true}
      groupedData={groupedData}
      renderGroupHeader={groupedData ? renderGroupHeader : undefined}
      variant="conflictResolution"
    />
  );
};
